import { empty, identical, phpCopy } from '../../php-compat';
import { at, num, f, record, values } from './php-shapes';
import { ftShapeSegmentHit3d } from './ft3d_shape_core';
import type { PortEnv } from '../../port-contract';
interface SightDeps {
    ft_shape_box_is_roofed(box: unknown): boolean;
    ft_shape_stair_contour(connection: unknown, box: unknown): unknown;
}
const EPS = 1e-6;
/** PHP: ft_shape_contour_cross (includes/functions/janus/encounter/ft3d_sight_core.php:43) */
export function ftShapeContourCross(a: unknown, b: unknown, p: unknown): unknown {
    return f((num(at(b, 'x')) - num(at(a, 'x'))) * (num(at(p, 'y')) - num(at(a, 'y')))
        - (num(at(b, 'y')) - num(at(a, 'y'))) * (num(at(p, 'x')) - num(at(a, 'x'))));
}
/** PHP: ft_shape_stair_aperture_at (includes/functions/janus/encounter/ft3d_sight_core.php:80) */
export function ftShapeStairApertureAt(shape: unknown, box: unknown, x: unknown, y: unknown, env: PortEnv<SightDeps>): boolean {
    for (const connection of values(at(shape, 'authoring', 'verticalConnections'))) {
        if ((at(connection, 'targetRoomId') ?? '') !== at(box, 'id')) continue;
        const contour = values(env.deps.ft_shape_stair_contour(connection, box)); if (contour.length < 3) continue;
        let inside = true;
        for (let i = 0; i < contour.length; i++) if (num(ftShapeContourCross(contour[i], contour[(i + 1) % contour.length], record({ x: f(num(x)), y: f(num(y)) }))) <= EPS) { inside = false; break; }
        if (inside) return true;
    }
    return false;
}
/** PHP: ft_shape_sight_segment_hit_3d (includes/functions/janus/encounter/ft3d_sight_core.php:4) */
export function ftShapeSightSegmentHit3d(shape: unknown, source: unknown, target: unknown, contain = false, env: PortEnv<SightDeps>): unknown {
    let best = ftShapeSegmentHit3d(shape, source, target, 'sight', contain, env);
    const dz = num(at(target, 'z')) - num(at(source, 'z')); if (Math.abs(dz) < EPS) return best;
    for (const box of values(at(shape, 'boxes'))) {
        if (!empty(at(box, 'navigationOpenGround'))) continue;
        const surfaces = [num(at(box, 'floor'))];
        if (env.deps.ft_shape_box_is_roofed(box)) surfaces.push(num(at(box, 'creation', 'physicalCeilingFt') ?? at(box, 'ceiling')));
        for (let index = 0; index < surfaces.length; index++) {
            const z = surfaces[index]!, t = (z - num(at(source, 'z'))) / dz;
            if (t <= EPS || t > 1 + EPS || t >= 1 - EPS && dz < 0 || best !== null && t >= num(at(best, 't'))) continue;
            const x = num(at(source, 'x')) + t * (num(at(target, 'x')) - num(at(source, 'x')));
            const y = num(at(source, 'y')) + t * (num(at(target, 'y')) - num(at(source, 'y')));
            if (x < num(at(box, 'x', 'min')) || x > num(at(box, 'x', 'max')) || y < num(at(box, 'y', 'min')) || y > num(at(box, 'y', 'max'))) continue;
            const interior = values(at(shape, 'boxes')).some(other => !identical(other, box) && empty(at(other, 'navigationOpenGround'))
                && x > num(at(other, 'x', 'min')) + EPS && x < num(at(other, 'x', 'max')) - EPS
                && y > num(at(other, 'y', 'min')) + EPS && y < num(at(other, 'y', 'max')) - EPS
                && z > num(at(other, 'floor')) + EPS && z < num(at(other, 'ceiling')) - EPS);
            if (interior) continue;
            if (index > 0 && values(at(shape, 'boxes')).some(upper => at(upper, 'id') !== at(box, 'id') && Math.abs(num(at(upper, 'floor')) - z) < .01
                && x >= num(at(upper, 'x', 'min')) && x <= num(at(upper, 'x', 'max')) && y >= num(at(upper, 'y', 'min')) && y <= num(at(upper, 'y', 'max')))) continue;
            if (index === 0 && ftShapeStairApertureAt(shape, box, x, y, env)) continue;
            best = record({ t: f(t), x: f(x), y: f(y), z: f(z), axis: 'z', at: f(z) });
        }
    }
    return phpCopy(best);
}
